import { InvoiceTemplateType, invoiceTemplateOptions } from './invoiceTemplates';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

// Helper function to format currency
const formatCurrency = (amount: number, currency: string = 'INR') => {
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 2
  }).format(amount || 0);

  // Replace Rupee symbol with Rs. because StandardFonts.Helvetica doesn't support it
  return formatted.replace(/₹/g, 'Rs. ');
};

/**
 * Generate a PDF for an invoice using pdf-lib (a non-React approach)
 * This avoids JSX syntax issues in TypeScript files
 */
export const generateInvoicePDF = async (invoice: any, userProfile: any, templateType?: InvoiceTemplateType | number) => {
  try {
    // Always use the AkMo Invoice template (ID: 1)
    const templateId = 1;
    // Find the AkMo template
    const akmoTemplate = invoiceTemplateOptions.find(t => t.id === templateId);

    // Ensure we have the correct color scheme
    const parseHexColor = (hex: string) => {
      const cleanHex = (hex || '#3949AB').replace('#', '');
      const r = parseInt(cleanHex.substring(0, 2), 16) / 255 || 0.22;
      const g = parseInt(cleanHex.substring(2, 4), 16) / 255 || 0.29;
      const b = parseInt(cleanHex.substring(4, 6), 16) / 255 || 0.67;
      return rgb(r, g, b);
    };

    const primaryBrandColor = parseHexColor(userProfile.primaryColor || '#3949AB');

    // Format dates properly
    const formattedInvoice = {
      ...invoice,
      date: invoice.invoiceDate || invoice.date || new Date().toISOString().split('T')[0],
      // Ensure other properties are available and correctly formatted
      items: invoice.items || [],
      total: invoice.total || invoice.amount || 0,
      subtotal: invoice.subtotal || 0,
      tax: invoice.tax || 0,
      discount: invoice.discount || 0,
      currency: invoice.currency || 'INR',
    };

    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();

    // Add a page to the document
    const page = pdfDoc.addPage([595, 842]); // A4 size

    // Get fonts
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Set some styles
    const fontSize = 12;
    const margin = 50;
    const lineHeight = 18;

    // Draw top brand bar (Zoho premium style)
    page.drawRectangle({
      x: 0,
      y: page.getHeight() - 8,
      width: page.getWidth(),
      height: 8,
      color: primaryBrandColor,
    });

    // Start drawing content
    let y = page.getHeight() - margin;

    // Embed logo if available
    if (userProfile.logoUrl) {
      try {
        let logoImage;
        const base64Data = userProfile.logoUrl.split(',')[1];
        if (userProfile.logoUrl.includes('image/png')) {
          logoImage = await pdfDoc.embedPng(base64Data);
        } else if (userProfile.logoUrl.includes('image/jpeg') || userProfile.logoUrl.includes('image/jpg')) {
          logoImage = await pdfDoc.embedJpg(base64Data);
        }
        
        if (logoImage) {
          const logoWidth = 100;
          const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
          page.drawImage(logoImage, {
            x: page.getWidth() - margin - logoWidth,
            y: y - 10,
            width: logoWidth,
            height: logoHeight,
          });
        }
      } catch (err) {
        console.error('Error embedding logo in PDF:', err);
      }
    }

    // Draw header
    page.drawText("INVOICE", {
      x: margin,
      y,
      size: 24,
      font: boldFont,
      color: primaryBrandColor,
    });

    y -= lineHeight * 2;

    // Draw company info
    page.drawText(`${userProfile.businessName || userProfile.name || 'Your Company'}`, {
      x: margin,
      y,
      size: fontSize,
      font: boldFont,
    });

    y -= lineHeight;

    if (userProfile.address) {
      page.drawText(`${userProfile.address}`, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    if (userProfile.email) {
      page.drawText(`Email: ${userProfile.email}`, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    if (userProfile.phone) {
      page.drawText(`Phone: ${userProfile.phone}`, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    if (userProfile.gstin) {
      page.drawText(`GSTIN: ${userProfile.gstin}`, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    y -= lineHeight;

    // Draw invoice info
    const rightColumnX = page.getWidth() - margin - 200;

    page.drawText(`Invoice #: ${formattedInvoice.invoiceNumber}`, {
      x: rightColumnX,
      y: page.getHeight() - margin - lineHeight * 2,
      size: fontSize,
      font: boldFont,
    });

    page.drawText(`Date: ${new Date(formattedInvoice.date).toLocaleDateString()}`, {
      x: rightColumnX,
      y: page.getHeight() - margin - lineHeight * 3,
      size: fontSize,
      font,
    });

    if (formattedInvoice.dueDate) {
      page.drawText(`Due Date: ${new Date(formattedInvoice.dueDate).toLocaleDateString()}`, {
        x: rightColumnX,
        y: page.getHeight() - margin - lineHeight * 4,
        size: fontSize,
        font,
      });
    }

    // Draw client info
    page.drawText("Bill To:", {
      x: margin,
      y,
      size: fontSize,
      font: boldFont,
    });

    y -= lineHeight;

    // Use client name or client.name - handle object vs string safely
    let clientName = '';
    if (formattedInvoice.client && typeof formattedInvoice.client === 'object') {
      clientName = formattedInvoice.client.name || 'Client';
    } else {
      clientName = String(formattedInvoice.client || '');
    }

    if (clientName) {
      page.drawText(clientName, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    // Safely extract address - check clientDetails first, then client object if applicable
    let clientAddress = '';
    if (formattedInvoice.clientDetails?.address) {
      clientAddress = formattedInvoice.clientDetails.address;
    } else if (formattedInvoice.client && typeof formattedInvoice.client === 'object' && formattedInvoice.client.address) {
      clientAddress = formattedInvoice.client.address;
    }

    if (clientAddress) {
      page.drawText(clientAddress, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    // Safely extract email
    let clientEmail = '';
    if (formattedInvoice.clientDetails?.email) {
      clientEmail = formattedInvoice.clientDetails.email;
    } else if (formattedInvoice.client && typeof formattedInvoice.client === 'object' && formattedInvoice.client.email) {
      clientEmail = formattedInvoice.client.email;
    }

    if (clientEmail) {
      page.drawText(`Email: ${clientEmail}`, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    // Safely extract GSTIN
    let clientGstin = '';
    if (formattedInvoice.clientDetails?.gstin) {
      clientGstin = formattedInvoice.clientDetails.gstin;
    } else if (formattedInvoice.client && typeof formattedInvoice.client === 'object' && formattedInvoice.client.gstin) {
      clientGstin = formattedInvoice.client.gstin;
    }

    if (clientGstin) {
      page.drawText(`GSTIN: ${clientGstin}`, {
        x: margin,
        y,
        size: fontSize,
        font,
      });
      y -= lineHeight;
    }

    y -= lineHeight;

    // Draw table header
    const col1X = margin;
    const col2X = margin + 200;
    const col3X = margin + 300;
    const col4X = margin + 400;
    const col5X = margin + 475;

    // Draw header line
    page.drawLine({
      start: { x: margin, y: y },
      end: { x: page.getWidth() - margin, y: y },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8),
    });

    y -= lineHeight;

    // Draw table headers
    page.drawText("Description", {
      x: col1X,
      y,
      size: fontSize,
      font: boldFont,
    });

    page.drawText("Quantity", {
      x: col2X,
      y,
      size: fontSize,
      font: boldFont,
    });

    page.drawText("Rate", {
      x: col3X,
      y,
      size: fontSize,
      font: boldFont,
    });

    page.drawText("Tax", {
      x: col4X,
      y,
      size: fontSize,
      font: boldFont,
    });

    page.drawText("Amount", {
      x: col5X,
      y,
      size: fontSize,
      font: boldFont,
    });

    y -= lineHeight;

    // Draw header line
    page.drawLine({
      start: { x: margin, y: y },
      end: { x: page.getWidth() - margin, y: y },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8),
    });

    y -= lineHeight;

    // Draw items
    if (formattedInvoice.items && formattedInvoice.items.length > 0) {
      formattedInvoice.items.forEach((item: any, index: number) => {
        // Use name or description for item display
        const itemName = item.name || item.description || "";
        page.drawText(itemName, {
          x: col1X,
          y,
          size: fontSize,
          font,
        });

        page.drawText(`${item.quantity || ""}`, {
          x: col2X,
          y,
          size: fontSize,
          font,
        });

        page.drawText(formatCurrency(item.rate, formattedInvoice.currency), {
          x: col3X,
          y,
          size: fontSize,
          font,
        });

        // Use gstRate, tax, or default to 0%
        const taxRate = item.gstRate || item.tax || 0;
        page.drawText(`${taxRate}%`, {
          x: col4X,
          y,
          size: fontSize,
          font,
        });

        page.drawText(formatCurrency(item.amount, formattedInvoice.currency), {
          x: col5X,
          y,
          size: fontSize,
          font,
        });

        y -= lineHeight;

        // Draw line after each item
        if (index < formattedInvoice.items.length - 1) {
          page.drawLine({
            start: { x: margin, y: y },
            end: { x: page.getWidth() - margin, y: y },
            thickness: 0.5,
            color: rgb(0.9, 0.9, 0.9),
          });

          y -= lineHeight / 2;
        }
      });
    }

    // Draw line after items
    page.drawLine({
      start: { x: margin, y: y },
      end: { x: page.getWidth() - margin, y: y },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8),
    });

    y -= lineHeight * 1.5;

    // Draw summary
    const summaryX = page.getWidth() - margin - 200;

    page.drawText("Subtotal:", {
      x: summaryX,
      y,
      size: fontSize,
      font: boldFont,
    });

    page.drawText(formatCurrency(formattedInvoice.subtotal, formattedInvoice.currency), {
      x: page.getWidth() - margin - 80,
      y,
      size: fontSize,
      font,
    });

    y -= lineHeight;

    if (formattedInvoice.discount > 0) {
      page.drawText("Discount:", {
        x: summaryX,
        y,
        size: fontSize,
        font: boldFont,
      });

      page.drawText(formatCurrency(formattedInvoice.discount, formattedInvoice.currency), {
        x: page.getWidth() - margin - 80,
        y,
        size: fontSize,
        font,
      });

      y -= lineHeight;
    }

    if (formattedInvoice.tax > 0) {
      page.drawText("Tax:", {
        x: summaryX,
        y,
        size: fontSize,
        font: boldFont,
      });

      page.drawText(formatCurrency(formattedInvoice.tax, formattedInvoice.currency), {
        x: page.getWidth() - margin - 80,
        y,
        size: fontSize,
        font,
      });

      y -= lineHeight;
    }

    y -= lineHeight / 2;

    // Draw total
    page.drawLine({
      start: { x: summaryX, y: y },
      end: { x: page.getWidth() - margin, y: y },
      thickness: 1,
      color: rgb(0.8, 0.8, 0.8),
    });

    y -= lineHeight;

    page.drawText("Total:", {
      x: summaryX,
      y,
      size: fontSize + 2,
      font: boldFont,
    });

    page.drawText(formatCurrency(formattedInvoice.total || formattedInvoice.amount, formattedInvoice.currency), {
      x: page.getWidth() - margin - 80,
      y,
      size: fontSize + 2,
      font: boldFont,
    });

    y -= lineHeight * 2;

    // Draw notes if available
    if (formattedInvoice.notes) {
      page.drawText("Notes:", {
        x: margin,
        y,
        size: fontSize,
        font: boldFont,
      });

      y -= lineHeight;

      page.drawText(formattedInvoice.notes, {
        x: margin,
        y,
        size: fontSize,
        font,
        maxWidth: page.getWidth() - margin * 2,
      });

      y -= lineHeight * 2;
    }

    // Draw terms if available
    if (formattedInvoice.terms || formattedInvoice.termsAndConditions) {
      page.drawText("Terms and Conditions:", {
        x: margin,
        y,
        size: fontSize,
        font: boldFont,
      });

      y -= lineHeight;

      page.drawText(formattedInvoice.terms || formattedInvoice.termsAndConditions || "Payment due within 30 days.", {
        x: margin,
        y,
        size: fontSize,
        font,
        maxWidth: page.getWidth() - margin * 2,
      });

      y -= lineHeight * 2;
    }

    // Draw Bank Details and QR Code
    const bankDetails = userProfile.bankDetails || formattedInvoice.bankDetails || {};
    const upiId = userProfile.upiId || bankDetails.upiId || '';
    const accNo = userProfile.accountNumber || bankDetails.accountNumber || '';
    const ifsc = userProfile.ifscCode || bankDetails.ifscCode || bankDetails.ifsc || '';
    const bankName = userProfile.bankName || bankDetails.bankName || '';

    if (accNo || upiId) {
      page.drawText("Payment Details:", {
        x: margin,
        y,
        size: fontSize,
        font: boldFont,
      });
      y -= lineHeight;

      if (bankName) {
        page.drawText(`Bank Name: ${bankName}`, { x: margin, y, size: fontSize, font });
        y -= lineHeight;
      }
      if (accNo) {
        page.drawText(`Account No: ${accNo}`, { x: margin, y, size: fontSize, font });
        y -= lineHeight;
      }
      if (ifsc) {
        page.drawText(`IFSC Code: ${ifsc}`, { x: margin, y, size: fontSize, font });
        y -= lineHeight;
      }
      if (upiId) {
        page.drawText(`UPI ID: ${upiId}`, { x: margin, y, size: fontSize, font });
        y -= lineHeight;
      }

      // Add QR Code if available
      let qrCodeImage;
      const upiQrImage = userProfile.upiQrImage || userProfile.qrCode || bankDetails.upiQrImage;
      try {
        if (upiQrImage) {
          const base64Data = upiQrImage.split(',')[1];
          if (upiQrImage.includes('image/png')) {
            qrCodeImage = await pdfDoc.embedPng(base64Data);
          } else if (upiQrImage.includes('image/jpeg') || upiQrImage.includes('image/jpg')) {
            qrCodeImage = await pdfDoc.embedJpg(base64Data);
          }
        } else if (upiId) {
          // Fetch dynamically generated QR code image as bytes
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`upi://pay?pa=${upiId}&pn=${encodeURIComponent(userProfile.businessName || userProfile.name || 'Business')}&am=${formattedInvoice.total || formattedInvoice.amount || 0}&cu=INR`)}`;
          const qrResponse = await fetch(qrUrl);
          const qrArrayBuffer = await qrResponse.arrayBuffer();
          qrCodeImage = await pdfDoc.embedPng(qrArrayBuffer);
        }
        
        if (qrCodeImage) {
          const qrSize = 80;
          // Draw it on the right side
          page.drawImage(qrCodeImage, {
            x: page.getWidth() - margin - qrSize,
            y: y + lineHeight * 4 - qrSize + 10,
            width: qrSize,
            height: qrSize,
          });
          page.drawText("Scan to Pay", {
            x: page.getWidth() - margin - qrSize + 10,
            y: y + lineHeight * 4 - qrSize - 5,
            size: 10,
            font: boldFont,
          });
        }
      } catch (err) {
        console.error('Error embedding QR Code in PDF:', err);
      }
      
      y -= lineHeight;
    }

    // Draw footer
    y = margin + lineHeight * 2;

    page.drawText("Thank you for your business!", {
      x: page.getWidth() / 2 - 80,
      y,
      size: fontSize,
      font: boldFont,
    });

    y -= lineHeight;

    page.drawText("This is a computer-generated document and does not require a signature.", {
      x: page.getWidth() / 2 - 180,
      y,
      size: fontSize - 2,
      font,
    });

    // Generate PDF
    const pdfBytes = await pdfDoc.save();
    return new Uint8Array(pdfBytes);
  } catch (error) {
    console.error('Error generating invoice PDF:', error);
    throw error;
  }
};
